/**
 * `SelectionSets_Registry.ts` — port of `AI/Registry/SelectionSets_Registry.cs`.
 */

import { AgentToolNames } from "./agentToolNames";
import {
  type AIAgentTool,
  buildParams,
  stringList,
  stringParam,
} from "./AIAgentToolRegistryModel";

export function addSelectionSetsTools(tools: AIAgentTool[]): void {
  tools.push(
    {
      name: AgentToolNames.CreateSelectionSet,
      category: "Authoring",
      labelWhenActive: "Creating selection set...",
      description:
        "Creates a named selection set from the given parts so they can be selected and manipulated as a group later. Resolve part targets first (see system prompt) and pass hierarchy paths via partPaths. When setName is omitted a default 'Set N' name is used.",
      humanDescription: "Group parts into a named selection set for later use.",
      parameters: buildParams({
        setName: stringParam("Name for the new selection set."),
        partPaths: stringList(
          "Hierarchy paths from Get_Scene_Context (e.g. '0,1,2') for the parts to include.",
          false,
        ),
      }),
      enabled: true,
      writerOnly: true,
    },
    {
      name: AgentToolNames.RemoveSelectionSet,
      category: "Authoring",
      labelWhenActive: "Removing selection set...",
      description:
        "Deletes a selection set by its exact name from Get_Scene_Context. The parts themselves are not affected.",
      humanDescription: "Delete a selection set without touching its parts.",
      parameters: buildParams({
        setName: stringParam("Exact selection set name from Get_Scene_Context.", false),
      }),
      enabled: true,
      writerOnly: true,
    },
    {
      name: AgentToolNames.CountParts,
      category: "Context",
      labelWhenActive: "Counting parts...",
      description:
        "Counts parts of one kind from the scene, and is the ONLY valid source for a count - never tally hierarchy lines yourself. "
        + "Counting is answered from SELECTION SETS: the author groups the countable parts into a set, and this returns that set's size, expanding merged nodes so the parts folded into one count individually. "
        + "For 'how many screws / HMIs / placards are there', pass partName - the kind of part in the user's own words. Every set is searched by what it CONTAINS, and by its own NAME only when nothing contains that part - so you do NOT need to look a set up first, and a set works whether its name describes its contents or is a bare 'Set 2'. "
        + "For 'how many of these are there' while the user points at or has selected a part, pass partPath and the set containing it is found for you. "
        + "If the scene has no selection sets, or none of them contains that part, the result says so: fall back to the project documents (Prompt_Search_Docs) and cite the document, and if that has no answer say you do not have that information. Do not estimate. "
        + "SILENT CALL - the accuracy rules above are your reasoning, not something to say: never announce that you are about to count, explain that you have to look the number up rather than estimate it, or narrate the fall back to the documents as you take it. Call with nothing spoken, and speak once, with the number. "
        + "If several sets could be meant the result lists them with a sample of their members - offer those samples with present_options, because a set's name may say nothing about what is in it.",
      humanDescription:
        "Count parts of one kind using the scene's selection sets.",
      parameters: buildParams({
        partName: stringParam(
          "The kind of part to count, in the user's own words ('screws', 'top screws', 'HMIs'). Matched against the parts each selection set CONTAINS, falling back to the set names only when no set contains a match.",
        ),
        partPath: stringParam(
          "Hierarchy path (e.g. '0,1,2') of the part the user pointed at or selected, for 'how many of these'.",
        ),
      }),
      enabled: true,
      writerOnly: false,
    },
  );
}
